/**
 * AskBar — persistent Ask Meridian input + Companion attention status.
 *
 * Replaces the old Ask Meridian primary-nav tab with a constant
 * search-style field. The whole point of an AI-first product is that
 * the AI is always reachable, not buried behind a route. User types
 * a question anywhere in the app, hits Enter, and lands on
 * /ask-meridian?q=… which auto-fires the question.
 *
 * Questions always hand off to the canonical /ask-meridian?q=… route so the
 * Ask Meridian page owns branch selection, history, execution and rendering.
 * The adjacent status control is deliberately separate: it opens Companion
 * work without turning the question input itself into a notification button.
 *
 * Keyboard: Cmd/Ctrl-K focuses the bar from anywhere. Standard
 * power-user shortcut familiar from Linear / Notion / Slack so the
 * AI feels like the universal input rather than a sidebar feature.
 */

import { useEffect, useRef, useState, type RefObject } from "react";
import { useNavigate } from "react-router";
import { Sparkles } from "lucide-react";

import { CompanionHeaderStatus } from "./companion/CompanionHeaderStatus";

interface AskBarProps {
  /** Shells can hide the whole command group while their workspace loads. */
  visible: boolean;
  /** The V3 shell uses a slightly warmer paper treatment. */
  warm?: boolean;
  /** Optional seed used by component previews and future contextual asks. */
  initialQuestion?: string;
  /** Exposes the submit action only in the isolated interaction preview. */
  previewSubmitVisible?: boolean;
}

interface AskBarInputProps {
  inputRef: RefObject<HTMLInputElement>;
  value: string;
  onChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  warm?: boolean;
  submitVisible?: boolean;
}

/** Presentational input shared by the live shell and the isolated QA preview. */
export function AskBarInput({
  inputRef,
  value,
  onChange,
  onSubmit,
  warm = false,
  submitVisible = false,
}: AskBarInputProps) {
  return (
    <form
      onSubmit={onSubmit}
      className={`group relative w-[min(34vw,360px)] min-w-[280px] ${
        warm ? "drop-shadow-[0_1px_1px_rgba(15,23,42,0.03)]" : ""
      }`}
    >
      <Sparkles
        className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-cyan-700"
        strokeWidth={2}
      />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ask Meridian…"
        aria-label="Ask Meridian"
        className={`block w-full rounded-lg border py-2 pl-9 pr-[4.75rem] text-[13px] text-slate-900 outline-none transition-[border-color,background-color,box-shadow] placeholder:text-slate-400 focus:border-cyan-300 focus:bg-white focus:ring-2 focus:ring-cyan-100 ${
          warm
            ? "border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
            : "border-slate-200 bg-slate-50"
        }`}
      />
      <button
        type="submit"
        tabIndex={submitVisible ? 0 : -1}
        className={
          submitVisible
            ? "absolute left-0 top-12 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white"
            : "sr-only"
        }
      >
        Ask Meridian
      </button>
      <span
        aria-hidden
        className="pointer-events-none absolute right-[3.8rem] top-1/2 h-4 w-px -translate-y-1/2 bg-slate-200"
      />
      <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[10px] text-slate-400">
        ⌘ K
      </kbd>
    </form>
  );
}

export function AskBar({
  visible,
  warm = false,
  initialQuestion = "",
  previewSubmitVisible = false,
}: AskBarProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState(initialQuestion);

  // Cmd/Ctrl-K from anywhere → focus the bar. Don't intercept when a
  // textarea / contenteditable is already focused (the user is typing
  // somewhere intentional). Listening on document keeps the shortcut
  // global without each page wiring its own handler.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isShortcut = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (!isShortcut) return;
      const active = document.activeElement;
      const tag = active?.tagName.toLowerCase();
      if (tag === "textarea" || (active as HTMLElement)?.isContentEditable) {
        return;
      }
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!visible) return null;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const q = value.trim();
    if (!q) {
      // Empty submit → still go to /ask, just without a query, so
      // the user can start a fresh session.
      navigate("/ask-meridian");
      return;
    }
    navigate(`/ask-meridian?q=${encodeURIComponent(q)}`);
    setValue("");
  }

  return (
    <div className="hidden min-w-0 items-center gap-2.5 md:flex">
      <AskBarInput
        inputRef={inputRef}
        value={value}
        onChange={setValue}
        onSubmit={submit}
        warm={warm}
        submitVisible={previewSubmitVisible}
      />

      <CompanionHeaderStatus dark={warm} />
    </div>
  );
}
